import { Box, Paper, Stack, Typography } from '@mui/material';
import { styled } from '~/themes';
import Grid from '@mui/material/Unstable_Grid2';
import { LinkButton } from '~/components/common/Button';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { usePaymentInfo } from '~/providers/PaymentProvider';
import { IPlanTier } from '~/models/plan';
import moment from 'moment';
import { Icon } from '~/components/common/Icon';
import { alertParams, showAlert } from '~/components/common/Alert';
import { queryKeys } from '~/services/api/queryKeys';
import { queryClient } from '~/lib/react-query';
import PenIcon from '~/assets/images/icons/pen.svg';
import { isEmpty } from 'lodash';
import { DialogUpdatePayment } from './DialogUpdatePayment';
import { BillingHistory } from './BillingHistory';

type BillingProps = {};

export const StyledBillingContainer = styled(Box)(({ theme }) => ({
  maxWidth: 824,
  minWidth: 824,
  flexGrow: 1,
  [theme.breakpoints.down('ss')]: {
    minWidth: 0,
  },
}));

export const StyledBillingItem = styled(Paper)(({ theme }) => ({
  border: '1px solid #E2E7F0',
  borderRadius: 8,
  background: theme.color.white,
  padding: theme.spacing(2.4),
}));

export const StyledBillingValue = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  fontWeight: 500,
  marginTop: theme.spacing(1),
  color: theme.color.violet500,
}));

export const StyledBillingTitle = styled(Typography)(({ theme }) => ({
  fontSize: 18,
  fontWeight: 500,
  color: theme.color.violet500,
}));

export const StyledBillingValueWrapper = styled(Box)(() => ({
  color: '#717D96',
}));

export const formatCardExpiry = (
  exp_month?: number,
  exp_year?: number,
  fullMonth: boolean = false,
): string => {
  if (!exp_month || !exp_year) return '';

  const formatStr = fullMonth ? 'MMMM YYYY' : 'MMM YYYY';
  return moment(`${exp_year}-${exp_month}`, 'YYYY-M').format(formatStr);
};

export const Billing: React.FC<BillingProps> = () => {
  const navigate = useNavigate();
  const [openUpdatePayment, setOpenUpdatePayment] = useState(false);
  const { org, currentPlan, isBundlePlan, additionalTask } = usePaymentInfo();
  const cardInfo = org?.card || {};

  return (
    <StyledBillingContainer>
      <Grid container spacing={1.6}>
        <Grid xs={12} md={6}>
          <StyledBillingItem>
            <Stack direction='row' justifyContent='space-between'>
              <StyledBillingTitle>My Plan</StyledBillingTitle>
              {!isBundlePlan && (
                <LinkButton
                  sx={{
                    textDecoration: 'unset',
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'row',
                    gap: '4px',
                    fontWeight: 500,
                  }}
                  onClick={() => navigate('../setting/plans')}
                >
                  <img src={PenIcon} alt='edit-icon' />
                  Update
                </LinkButton>
              )}
            </Stack>
            <Stack direction='row' mt={2}>
              <StyledBillingValueWrapper
                sx={{
                  width: '50%',
                }}
              >
                <Typography fontSize={12}>Included tasks</Typography>
                <StyledBillingValue>
                  {isBundlePlan
                    ? 'Unlimited'
                    : `${org.limit_task || 50} tasks/mo`}
                </StyledBillingValue>
              </StyledBillingValueWrapper>

              <StyledBillingValueWrapper>
                <Typography fontSize={12}>Additional tasks</Typography>

                {currentPlan.tier === IPlanTier.Free ? (
                  <StyledBillingValue>Not available</StyledBillingValue>
                ) : (
                  <StyledBillingValue>
                    {isBundlePlan ? '-' : additionalTask || '0'}
                  </StyledBillingValue>
                )}
              </StyledBillingValueWrapper>
            </Stack>
          </StyledBillingItem>
        </Grid>

        <Grid xs={12} md={6}>
          <StyledBillingItem>
            <Stack direction='row' justifyContent='space-between'>
              <StyledBillingTitle>
                Payment Method{' '}
                {org?.grace_period && (
                  <Icon
                    name='exclamation'
                    useBackgroundImg
                    size={14}
                    style={{ marginBottom: -1 }}
                  />
                )}
              </StyledBillingTitle>
              {!isBundlePlan && (
                <LinkButton
                  sx={{
                    textDecoration: 'unset',
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'row',
                    gap: '4px',
                    fontWeight: 500,
                  }}
                  onClick={() => setOpenUpdatePayment(true)}
                >
                  <img src={PenIcon} alt='edit-icon' />
                  Update
                </LinkButton>
              )}
            </Stack>
            <Stack direction='row' mt={2}>
              <StyledBillingValueWrapper
                sx={{
                  width: '50%',
                }}
              >
                <Typography fontSize={12}>Credit Card</Typography>
                <StyledBillingValue>
                  {isBundlePlan || isEmpty(cardInfo)
                    ? '-'
                    : `**** **** **** ${cardInfo.last4}`}
                </StyledBillingValue>
              </StyledBillingValueWrapper>

              <StyledBillingValueWrapper
                sx={{
                  width: '50%',
                }}
              >
                <Typography fontSize={12}>Expired</Typography>
                <StyledBillingValue>
                  {isBundlePlan || isEmpty(cardInfo)
                    ? '-'
                    : `${
                        cardInfo?.exp_month?.toString().length === 1
                          ? `0${cardInfo?.exp_month?.toString()}`
                          : cardInfo?.exp_month?.toString()
                      }
                  /${cardInfo?.exp_year?.toString().slice(2, 4)}`}
                </StyledBillingValue>
              </StyledBillingValueWrapper>
            </Stack>
          </StyledBillingItem>
        </Grid>

        <Grid xs={12}>
          <StyledBillingItem>
            <StyledBillingTitle
              sx={{
                marginBottom: 1.6,
              }}
            >
              Billing History
            </StyledBillingTitle>
            <BillingHistory />
          </StyledBillingItem>
        </Grid>
      </Grid>
      <DialogUpdatePayment
        open={openUpdatePayment}
        onClose={() => setOpenUpdatePayment(false)}
        onUpdatePaymentSuccess={() => {
          setOpenUpdatePayment(false);
          queryClient.invalidateQueries([queryKeys.orgManagement.org]);
          showAlert({
            ...alertParams.success,
            title: 'Update Successfully',
          });
        }}
      />
    </StyledBillingContainer>
  );
};
